import { toast } from 'sonner'

import { createLogger } from '@/lib/logger'

import { downloadFile, type SaveOutcome } from './tauri'

const logger = createLogger('lib/utils/download')

/**
 * Collapse the user's home directory to `~` so the path stays readable in a
 * toast. Falls back to the full path if the home directory can't be resolved.
 */
async function toDisplayPath(path: string): Promise<string> {
  try {
    const { homeDir } = await import('@tauri-apps/api/path')
    const home = (await homeDir()).replace(/[/\\]+$/, '')
    if (home && path.startsWith(home)) {
      return `~${path.slice(home.length)}`
    }
  } catch (error) {
    logger.warn('Failed to resolve home directory for display path:', error)
  }
  return path
}

/** Open the saved file's folder in Finder / File Explorer. */
async function revealSavedFile(path: string): Promise<void> {
  try {
    const { revealItemInDir } = await import('@tauri-apps/plugin-opener')
    await revealItemInDir(path)
  } catch (error) {
    logger.error('Failed to reveal downloaded file:', error)
    toast.error('Could not open the containing folder')
  }
}

/**
 * Tell the user where a downloaded file landed.
 *
 * Desktop only by design. A browser download already surfaces its destination
 * in the browser's own download UI, and a cancelled save needs no announcing,
 * so both stay silent rather than adding noise.
 */
export async function notifySaveOutcome(outcome: SaveOutcome): Promise<void> {
  if (outcome.status !== 'saved') return

  toast.success('Download complete', {
    description: await toDisplayPath(outcome.path),
    // Sticky: where a file landed is worth keeping on screen until the user is
    // done with it. `closeButton` is required alongside this, otherwise a toast
    // that never times out has no obvious way to dismiss it.
    duration: Infinity,
    closeButton: true,
    action: {
      label: 'Show in folder',
      onClick: () => {
        void revealSavedFile(outcome.path)
      },
    },
  })
}

/**
 * `downloadFile` plus a toast naming the save location on desktop.
 *
 * This is the default entry point for user-initiated downloads. Reach for
 * `downloadFile` directly only when the caller needs to react to the outcome
 * instead of just reporting it.
 */
export async function downloadFileWithFeedback(
  url: string,
  filename?: string,
): Promise<SaveOutcome> {
  const outcome = await downloadFile(url, filename)
  await notifySaveOutcome(outcome)
  return outcome
}
